'use client';

import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import {
  FileSpreadsheet,
  CircleHelp,
  Calculator,
  BadgePoundSterling,
  Unplug,
  Clock,
  AlertTriangle,
  X,
} from 'lucide-react';

const problems = [
  {
    icon: FileSpreadsheet,
    title: 'Static Spreadsheet Templates',
    description:
      "NCSC spreadsheet templates don't scale for multi-system OT environments.",
    severity: 'high',
  },
  {
    icon: CircleHelp,
    title: 'No Contextual IGP Guidance',
    description:
      'Teams must interpret ~300 IGPs without plain-English help or OT-specific context.',
    severity: 'high',
  },
  {
    icon: Calculator,
    title: 'Manual Status Rollup',
    description:
      'Calculating status across IGPs, Contributing Outcomes, Principles, and Objectives is error-prone.',
    severity: 'medium',
  },
  {
    icon: BadgePoundSterling,
    title: 'External Consultancy Dependency',
    description:
      'External CAF engagements typically cost £15,000–£50,000 each, with findings that stay inside the consultant\'s deck.',
    severity: 'critical',
  },
  {
    icon: Unplug,
    title: 'No Workflow Integration',
    description:
      "Assessment findings don't flow into remediation tracking or issue management.",
    severity: 'medium',
  },
  {
    icon: Clock,
    title: 'Point-in-Time Only',
    description:
      'Spreadsheet-based assessments capture a moment in time with no mechanism for continuous reassessment.',
    severity: 'medium',
  },
];

export function ProblemSection() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  return (
    <section className="relative overflow-hidden py-24 lg:py-32" id="problem">
      {/* Background */}
      <div className="absolute inset-0 bg-background" />
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="absolute right-0 top-0 h-[500px] w-[500px] -translate-y-1/2 translate-x-1/2 rounded-full bg-destructive/5 blur-3xl" />

      <div
        ref={containerRef}
        className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
      >
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mx-auto max-w-3xl text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-destructive/20 bg-destructive/5 px-4 py-1.5"
          >
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <span className="font-heading text-sm font-medium text-destructive">
              The Problem
            </span>
          </motion.div>

          <h2 className="font-display text-3xl tracking-tight-display text-foreground sm:text-4xl lg:text-5xl">
            Six recurring barriers to{' '}
            <span className="italic text-destructive">CAF compliance</span>
          </h2>

          <p className="mt-6 text-lg text-muted-foreground">
            Organisations completing CAF assessments face the same structural
            barriers regardless of sector — they stem from the tools, not the teams.
          </p>
        </motion.div>

        {/* Cost Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mx-auto mt-16 max-w-2xl"
        >
          <div className="relative overflow-hidden rounded-2xl border border-destructive/20 bg-gradient-to-br from-destructive/5 to-transparent p-8 text-center">
            <div className="absolute inset-0 bg-grid-dense opacity-50" />
            <div className="relative">
              <p className="font-heading text-sm font-semibold uppercase tracking-wide-caps text-destructive">
                External CAF Consultancy
              </p>
              <p className="mt-3 font-display text-5xl tracking-tight-display text-foreground sm:text-6xl lg:text-7xl">
                £15K–£50K
              </p>
              <p className="mt-3 text-muted-foreground">
                typical cost per external engagement
              </p>
              <p className="mt-2 text-xs text-muted-foreground/60">
                Industry range — findings remain inside the consultant&apos;s deliverable
              </p>
            </div>
          </div>
        </motion.div>

        {/* Problems Grid */}
        <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {problems.map((problem, index) => {
            const Icon = problem.icon;
            return (
              <motion.div
                key={problem.title}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                className="group relative rounded-xl border border-border bg-card p-6 transition-all duration-300 hover:border-destructive/30 hover:shadow-lg"
              >
                {/* X mark */}
                <div className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-destructive/10">
                  <X className="h-3.5 w-3.5 text-destructive" />
                </div>

                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary transition-colors duration-300 group-hover:bg-destructive/10">
                  <Icon
                    className="h-6 w-6 text-muted-foreground transition-colors duration-300 group-hover:text-destructive"
                    strokeWidth={1.5}
                  />
                </div>

                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground">
                  {problem.title}
                </h3>

                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {problem.description}
                </p>

                {/* Severity indicator */}
                <div className="mt-4">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      problem.severity === 'critical'
                        ? 'bg-destructive/10 text-destructive'
                        : problem.severity === 'high'
                          ? 'bg-warning/10 text-warning'
                          : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {problem.severity === 'critical'
                      ? 'High Cost'
                      : problem.severity === 'high'
                        ? 'High Impact'
                        : 'Ongoing Impact'}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
